import axios from "axios";
import { getCookie, setCookie, deleteCookie } from "../utils/cookie";
import apiRoutes from "../api/api_route";

export const clearAuthCookies = () => {
  [
    "accessToken",
    "refreshToken",
    "accessExpiresIn",
    "refreshExpiresIn",
  ].forEach(deleteCookie);
};

export const resetDataAndLogout = () => {
  clearAuthCookies();
  window.location.href = "/";
};

export const setLoginCookies = (data) => {
  const accessSec = data.accessExpiresIn || 1800;
  const refreshSec = data.refreshExpiresIn || 2400;
  setCookie("accessToken", data.accessToken || data.token, accessSec / 86400);
  setCookie("refreshToken", data.refreshToken, refreshSec / 86400);
  setCookie("accessExpiresIn", String(accessSec), accessSec / 86400);
  setCookie("refreshExpiresIn", String(refreshSec), refreshSec / 86400);
  notifyLoginSession(data.accessToken || data.token || "");
};

// Phase 6: the socket needs to know when an officer's login access token
// rotates so its NEXT handshake authenticates with the fresh token.
const loginSessionSubscribers = new Set();

function notifyLoginSession(accessToken) {
  loginSessionSubscribers.forEach((fn) => {
    try {
      fn({ accessToken });
    } catch (err) {}
  });
}

export const subscribeToLoginSessionUpdates = (fn) => {
  loginSessionSubscribers.add(fn);
  return () => loginSessionSubscribers.delete(fn);
};

let refreshPromise = null;

const AUTH_PATH_PATTERNS = ["login", "verify"];
const GUEST_PATH_PATTERNS = ["lobby", "meeting", "meet"];

export const isGuestRoute = () => {
  const path = (window.location.pathname || "").toLowerCase();
  if (path === "/" || path === "/index.html") return true;
  return (
    AUTH_PATH_PATTERNS.some((p) => path.includes(p)) ||
    GUEST_PATH_PATTERNS.some((p) => path.includes(p))
  );
};

// Path-independence is the Phase 6 fix: an authenticated officer (refresh
// cookie present) must silently rotate the login access token inside
// lobby/meeting rooms too, not just on dashboard routes. True guests have no
// session cookies here and fall through to the external-token refresh path.
export const hasRefreshableSession = () => {
  return Boolean(
    getCookie("refreshExpiresIn") ||
      getCookie("refreshToken"),
  );
};

export const getRefreshToken = () => {
  if (refreshPromise) return refreshPromise;

  const REQUEST_TIMEOUT_MS = 10 * 1000;

  refreshPromise = (async () => {
    const token = getCookie("refreshToken");

    const attempts = [
      ...(token
        ? [
            {
              label: "auth-header",
              headers: { Authorization: `Bearer ${token}` },
              body: {},
              withCredentials: true,
            },
            {
              label: "request-body",
              headers: {},
              body: { refreshToken: token },
              withCredentials: true,
            },
          ]
        : []),
      {
        label: "httpOnly-cookie",
        headers: token ? {} : { "X-Client-Refresh": "1" },
        body: {},
        withCredentials: true,
      },
    ];

    for (const attempt of attempts) {
      try {
        const res = await axios.post(
          apiRoutes.auth.refreshToken,
          attempt.body,
          {
            headers: attempt.headers,
            withCredentials: attempt.withCredentials,
            timeout: REQUEST_TIMEOUT_MS,
          },
        );

        const d = { ...(res.data?.data || {}), ...(res.data || {}) };
        const newAccess =
          d.accessToken || d.token || d.newAccessToken || d.newToken;

        if (newAccess) {
          setLoginCookies({
            accessToken: newAccess,
            refreshToken: d.refreshToken || token,
            accessExpiresIn: d.accessExpiresIn,
            refreshExpiresIn: d.refreshExpiresIn,
          });
          return newAccess;
        }

        console.warn(
          `[Session] Refresh (${attempt.label}) returned 200 but no token:`,
          res.data,
        );
      } catch (err) {
        if (err.code === "ECONNABORTED") {
          console.error(
            `[Session] Refresh (${attempt.label}) timed out after ${REQUEST_TIMEOUT_MS}ms`,
          );
        } else {
          console.error(
            `[Session] Refresh (${attempt.label}) failed:`,
            err.response?.status,
            err.response?.data || err.message,
          );
        }
      }
    }

    return null;
  })();

  refreshPromise.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};
